import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LocalShipping,
  Receipt,
  Build,
  Warning,
} from '@mui/icons-material';
import { Dashboard as DashboardType } from '../types';
import { relatorioService } from '../services/relatorioService';
import { getErrorMessage } from '../utils/errors';

const Dashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await relatorioService.getDashboard();
      setDashboard(data);
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const cards = [
    {
      title: 'Total de Veículos',
      value: dashboard?.totalVeiculos || 0,
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      path: '/veiculos',
    },
    {
      title: 'Solicitações Pendentes',
      value: dashboard?.solicitacoesPendentes || 0,
      icon: <Receipt sx={{ fontSize: 40 }} />,
      color: '#f57c00',
      path: '/solicitacoes',
    },
    {
      title: 'Manutenções',
      value: dashboard?.solicitacoesAprovadas || 0,
      icon: <Build sx={{ fontSize: 40 }} />,
      color: '#388e3c',
      path: '/manutencoes',
    },
    {
      title: 'Custo Total',
      value: `R$ ${((dashboard?.custoTotalAbastecimentos || 0) + (dashboard?.custoTotalManutencoes || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: <Warning sx={{ fontSize: 40 }} />,
      color: '#d32f2f',
      path: '/relatorios',
    },
  ];

  return (
    <Container disableGutters maxWidth={false}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Bem-vindo, {user?.nome}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Painel de controle do sistema de gestão de frota
        </Typography>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Grid container spacing={3}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
              onClick={() => navigate(card.path)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
                  <Box sx={{ color: card.color, flexShrink: 0 }}>{card.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" fontWeight="bold" noWrap>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {card.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {dashboard && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumo Financeiro
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>Abastecimentos:</Typography>
                    <Typography fontWeight="bold">
                      R$ {dashboard.custoTotalAbastecimentos?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>Manutenções:</Typography>
                    <Typography fontWeight="bold">
                      R$ {dashboard.custoTotalManutencoes?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status dos Veículos
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>Total:</Typography>
                    <Typography fontWeight="bold">{dashboard.totalVeiculos}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>Ativos:</Typography>
                    <Typography fontWeight="bold" color="success.main">{dashboard.veiculosAtivos}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;
